import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { downloadFile, viewImage } from './api';

function SharedResource({ user, onLogin }) {
  const { type, id, filename } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, error, success, unauthorized
  const [errorMsg, setErrorMsg] = useState('');
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    const fetchResource = async () => {
      try {
        let res;
        if (type === 'file') {
          res = await downloadFile(id, filename);
        } else if (type === 'image') {
          res = await viewImage(id, filename);
        } else {
          setStatus('error');
          setErrorMsg('Invalid resource type');
          return;
        }

        // Handle Success
        const blob = new Blob([res.data], { 
            type: res.headers['content-type'] 
        });
        const url = window.URL.createObjectURL(blob);

        if (type === 'file') {
          // Trigger download
          const a = document.createElement('a');
          a.href = url;
          
          // Try to get filename from header or param
          let downloadName = filename || 'download';
          const disposition = res.headers['content-disposition'];
          if (disposition) {
            const match = disposition.match(/filename="?([^";]+)"?/);
            if (match) downloadName = match[1];
          }
          
          a.download = downloadName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setStatus('success');
          setErrorMsg('Download started...');
        } else {
          // Show image
          setImageUrl(url);
          setStatus('success');
        }

      } catch (err) {
        console.error("Resource fetch error:", err);
        if (err.response && err.response.status === 401) {
          setStatus('unauthorized');
        } else if (err.response && err.response.status === 403) {
          setStatus('error');
          setErrorMsg('Access Denied: You do not have permission to view this resource.');
        } else if (err.response && err.response.status === 404) {
          setStatus('error');
          setErrorMsg('Resource not found.');
        } else {
          setStatus('error');
          setErrorMsg('An error occurred while fetching the resource.');
        }
      }
    };

    fetchResource();
  }, [type, id, filename, user]); // Re-run if user changes (login happens)

  if (status === 'loading') {
    return (
      <div className="container text-center" style={{ marginTop: '4rem' }}>
        <div className="spinner"></div>
        <p>Loading resource...</p>
      </div>
    );
  }

  if (status === 'unauthorized') {
    return (
      <div className="container text-center" style={{ marginTop: '4rem' }}>
        <h2>🔒 Authentication Required</h2>
        <p>You need to be logged in to access this resource.</p>
        <button 
            className="btn btn-primary" 
            onClick={onLogin} 
        >
            Log In
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="container text-center" style={{ marginTop: '4rem' }}>
        <h2 style={{ color: 'var(--danger)' }}>Error</h2>
        <p>{errorMsg}</p>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  if (status === 'success' && type === 'image') {
    return (
      <div className="container text-center" style={{ marginTop: '2rem' }}>
        <img src={imageUrl} alt={filename || 'Shared Image'} style={{ maxWidth: '100%', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
        <div style={{ marginTop: '1rem' }}>
            <a href={imageUrl} download={filename || 'image'} className="btn btn-primary">Download Image</a>
        </div>
      </div>
    );
  }

  return (
    <div className="container text-center" style={{ marginTop: '4rem' }}>
      <h2>Success</h2>
      <p>{errorMsg}</p>
      <button className="btn btn-secondary" onClick={() => navigate('/')}>Go Home</button>
    </div>
  );
}

export default SharedResource;
